import dayjs from 'dayjs';
import {groupBy, prop} from 'ramda';

import type {DosageDetails, ResidentMedication} from '@/services/graphqlHooks';
import {PartOfTheDay, TimeType} from '@/services/graphqlHooks';
import {getPartOfTheDayFromTimestamp} from '@/shared/tasks/utils/getForecastedTasks';

export type DosageDetailsWithPartOfTheDay = Omit<DosageDetails, 'timeOfDay'> & {
  timeOfDay: PartOfTheDay;
};

export type MedicationWithPartOfTheDay = ResidentMedication & {
  [PartOfTheDay.Morning]: DosageDetailsWithPartOfTheDay[];
  [PartOfTheDay.Afternoon]: DosageDetailsWithPartOfTheDay[];
  [PartOfTheDay.Evening]: DosageDetailsWithPartOfTheDay[];
  [PartOfTheDay.Night]: DosageDetailsWithPartOfTheDay[];
};

const getTimestampFromTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return dayjs().hour(hours).minute(minutes).second(0).valueOf();
};

export const getMedicationWithPartOfTheDay = (
  residentMedication: ResidentMedication[]
): MedicationWithPartOfTheDay[] =>
  residentMedication.map(medication => {
    const {schedule} = medication || {};

    const dosageWithPartOfTheDay = schedule?.dosageDetails?.map(dosage => {
      return {
        ...dosage,
        timeOfDay:
          dosage?.timeType === TimeType.Specific
            ? getPartOfTheDayFromTimestamp(getTimestampFromTime(dosage.time!))
            : dosage?.timeOfDay,
      };
    }) as DosageDetailsWithPartOfTheDay[];

    const dosageByPartOfTheDay = groupBy(
      prop('timeOfDay'),
      dosageWithPartOfTheDay || []
    ) as Record<PartOfTheDay, DosageDetailsWithPartOfTheDay[]>;

    return {
      ...medication,
      [PartOfTheDay.Morning]: dosageByPartOfTheDay[PartOfTheDay.Morning] || [],
      [PartOfTheDay.Afternoon]:
        dosageByPartOfTheDay[PartOfTheDay.Afternoon] || [],
      [PartOfTheDay.Evening]: dosageByPartOfTheDay[PartOfTheDay.Evening] || [],
      [PartOfTheDay.Night]: dosageByPartOfTheDay[PartOfTheDay.Night] || [],
    };
  });
